import { getRuntimeDeviceLanguageTag } from "@/clients/runtime.client";
import { LANGUAGES } from "@/languages";
import { isNativeApp } from "@/platform";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";
import {
  APP_LOCALES,
  getAppLocaleDirection,
  normalizeAppLocale,
  selectAppLocale,
  type AppLocale,
} from "./policy";

type TranslationResource = Record<string, unknown>;
type LocaleModule = { default: TranslationResource };
type LazyAppLocale = Exclude<AppLocale, "en">;

const DEVICE_LANGUAGE_TIMEOUT_MS = 1_500;
const LOCALE_MODULE_TIMEOUT_MS = 5_000;

// Explicit by design: adding a shipped locale requires one obvious loader and
// one policy entry. Vite turns each import into an independently cached chunk.
const _localeLoaders = {
  ar: () => import("./ar.json"),
  bg: () => import("./bg.json"),
  bn: () => import("./bn.json"),
  cs: () => import("./cs.json"),
  da: () => import("./da.json"),
  de: () => import("./de.json"),
  el: () => import("./el.json"),
  es: () => import("./es.json"),
  et: () => import("./et.json"),
  fi: () => import("./fi.json"),
  fr: () => import("./fr.json"),
  he: () => import("./he.json"),
  hi: () => import("./hi.json"),
  hr: () => import("./hr.json"),
  hu: () => import("./hu.json"),
  id: () => import("./id.json"),
  it: () => import("./it.json"),
  ja: () => import("./ja.json"),
  ko: () => import("./ko.json"),
  lt: () => import("./lt.json"),
  nl: () => import("./nl.json"),
  no: () => import("./no.json"),
  pl: () => import("./pl.json"),
  pt: () => import("./pt.json"),
  ro: () => import("./ro.json"),
  ru: () => import("./ru.json"),
  sl: () => import("./sl.json"),
  sr: () => import("./sr.json"),
  sv: () => import("./sv.json"),
  sw: () => import("./sw.json"),
  th: () => import("./th.json"),
  tr: () => import("./tr.json"),
  uk: () => import("./uk.json"),
  vi: () => import("./vi.json"),
  zh: () => import("./zh.json"),
} satisfies Record<LazyAppLocale, () => Promise<LocaleModule>>;

export const appLanguages = APP_LOCALES.map((locale) =>
  LANGUAGES.find((language) => language.value === locale),
).filter(
  (language): language is NonNullable<typeof language> => Boolean(language),
);

export type LocaleInitializationFailure = {
  stage: "preference" | "deviceLanguage" | "localeModule";
  reason: "invalid" | "loadFailed" | "timeout" | "unavailable";
  language?: AppLocale;
};

export type LocaleInitializationResult = {
  language: AppLocale;
  requestedLanguage: AppLocale;
  usedFallback: boolean;
};

type LocaleInitializationOptions = {
  onFailure?: (failure: LocaleInitializationFailure) => void;
};

type SettledWithin<Value> =
  | { status: "resolved"; value: Value }
  | { status: "rejected" }
  | { status: "timeout" };

const _settleWithin = async <Value,>(
  pending: Promise<Value>,
  timeoutMs: number,
): Promise<SettledWithin<Value>> => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const outcome = await Promise.race<SettledWithin<Value>>([
    pending.then(
      (value) => ({ status: "resolved", value }),
      () => ({ status: "rejected" }),
    ),
    new Promise((resolve) => {
      timeout = setTimeout(() => resolve({ status: "timeout" }), timeoutMs);
    }),
  ]);
  if (timeout !== undefined) clearTimeout(timeout);
  return outcome;
};

const _report = (
  options: LocaleInitializationOptions,
  failure: LocaleInitializationFailure,
) => {
  try {
    options.onFailure?.(failure);
  } catch {
    // Locale startup must not depend on diagnostics.
  }
};

const _readStoredLocale = (
  options: LocaleInitializationOptions,
): string | undefined => {
  try {
    return window.localStorage.getItem("locale") || undefined;
  } catch {
    _report(options, { stage: "preference", reason: "unavailable" });
    return undefined;
  }
};

const _storeLocale = (
  locale: AppLocale,
  options: LocaleInitializationOptions,
) => {
  try {
    window.localStorage.setItem("locale", locale);
  } catch {
    _report(options, { stage: "preference", reason: "unavailable", language: locale });
  }
};

const _clearStoredLocale = (options: LocaleInitializationOptions) => {
  try {
    window.localStorage.removeItem("locale");
  } catch {
    _report(options, { stage: "preference", reason: "unavailable" });
  }
};

const _resolveDeviceLocale = async (
  options: LocaleInitializationOptions,
): Promise<AppLocale | undefined> => {
  const outcome = await _settleWithin(
    getRuntimeDeviceLanguageTag(),
    DEVICE_LANGUAGE_TIMEOUT_MS,
  );
  if (outcome.status !== "resolved") {
    _report(options, {
      stage: "deviceLanguage",
      reason: outcome.status === "timeout" ? "timeout" : "unavailable",
    });
    return undefined;
  }
  const locale = normalizeAppLocale(outcome.value);
  if (!locale) {
    _report(options, { stage: "deviceLanguage", reason: "invalid" });
  }
  return locale;
};

const _loadLocale = async (
  language: AppLocale,
  options: LocaleInitializationOptions,
): Promise<{ language: AppLocale; resource: TranslationResource }> => {
  if (language === "en") return { language, resource: en };
  const outcome = await _settleWithin(
    _localeLoaders[language](),
    LOCALE_MODULE_TIMEOUT_MS,
  );
  if (outcome.status === "resolved") {
    return { language, resource: outcome.value.default };
  }
  _report(options, {
    stage: "localeModule",
    reason: outcome.status === "timeout" ? "timeout" : "loadFailed",
    language,
  });
  return { language: "en", resource: en };
};

const _activateLocale = async (
  language: AppLocale,
  resource: TranslationResource,
) => {
  if (!i18n.isInitialized) {
    const resources: Record<
      string,
      { translation: TranslationResource }
    > = { en: { translation: en } };
    if (language !== "en") resources[language] = { translation: resource };
    await i18n.use(initReactI18next).init({
      resources,
      lng: language,
      fallbackLng: "en",
      supportedLngs: APP_LOCALES,
      compatibilityJSON: "v4",
      interpolation: { escapeValue: false },
    });
  } else {
    i18n.addResourceBundle("en", "translation", en, true, true);
    if (language !== "en") {
      i18n.addResourceBundle(
        language,
        "translation",
        resource,
        true,
        true,
      );
    }
    await i18n.changeLanguage(language);
  }
  if (typeof document === "object") {
    document.documentElement.lang = language;
    document.documentElement.dir = getAppLocaleDirection(language);
  }
};

const _initializeLocales = async (
  options: LocaleInitializationOptions,
): Promise<LocaleInitializationResult> => {
  if (typeof window !== "object") {
    await _activateLocale("en", en);
    return { language: "en", requestedLanguage: "en", usedFallback: false };
  }

  // appLang is the one key that means "the language of the interface", and
  // every surface uses it: the website's app links, the guest handoff, and the
  // in-app language picker. ?lang is deliberately NOT read here — it names a
  // content language elsewhere (/translate?lang=fr is the language a transcript
  // was translated into, not the language of the UI).
  const rawUrlLocale = new URLSearchParams(window.location.search).get("appLang");
  const urlLocale = normalizeAppLocale(rawUrlLocale);
  if (rawUrlLocale && !urlLocale) {
    _report(options, { stage: "preference", reason: "invalid" });
  }

  const rawStoredLocale = _readStoredLocale(options);
  const storedLocale = normalizeAppLocale(rawStoredLocale);
  if (rawStoredLocale && !storedLocale) {
    _report(options, { stage: "preference", reason: "invalid" });
    _clearStoredLocale(options);
  }
  if (urlLocale) _storeLocale(urlLocale, options);
  else if (storedLocale && storedLocale !== rawStoredLocale) {
    _storeLocale(storedLocale, options);
  }

  const deviceLocale =
    !urlLocale && !storedLocale && isNativeApp()
      ? await _resolveDeviceLocale(options)
      : undefined;
  const requestedLanguage = selectAppLocale({
    url: urlLocale,
    stored: storedLocale,
    device: deviceLocale,
    browser: navigator.language,
  });
  const loaded = await _loadLocale(requestedLanguage, options);
  await _activateLocale(loaded.language, loaded.resource);
  return {
    language: loaded.language,
    requestedLanguage,
    usedFallback: loaded.language !== requestedLanguage,
  };
};

let _initialization: Promise<LocaleInitializationResult> | undefined;

export const initializeLocales = (
  options: LocaleInitializationOptions = {},
): Promise<LocaleInitializationResult> => {
  _initialization ||= _initializeLocales(options);
  return _initialization;
};

export { i18n };
